// Top bar: title + connection-status pill driven by useHealth().
// AGENT-L: header refresh — left title + breadcrumb, right side adds a
// prominent "View Raw Tables" button that emits a window-level custom
// event for Agent R's drawer to listen on.

import { useHealth } from "../api/hooks";
import { useFilterStore, type FilterEntry, type FilterKey } from "../state/filterStore";
import "./TopBar.css";

// P1-pills: render a compact label for a single active filter entry.
function formatPillLabel(key: FilterKey, entry: FilterEntry): string {
  // Key shape is "<table>.<column>".
  const [table, ...rest] = key.split(".");
  const column = rest.join(".") || "";
  const head = column ? `${table}.${column}` : table;

  const op = entry.op;
  const raw = entry.value;

  // P1-pills: arrays — show first 2 + "…"
  let valueText: string;
  if (Array.isArray(raw)) {
    const first = raw.slice(0, 2).map((v) => String(v)).join(", ");
    valueText = raw.length > 2 ? `${first}…` : first;
  } else if (raw === null || raw === undefined) {
    valueText = "∅";
  } else {
    valueText = String(raw);
  }

  // P1-pills: include the op when it's not the default eq/in.
  if (op !== "eq" && op !== "in") {
    return `${head} ${op} ${valueText}`;
  }
  return `${head}: ${valueText}`;
}

export function TopBar() {
  const { data, status, isFetching } = useHealth();
  const clearAll = useFilterStore((s) => s.clearAll);
  const removeFilter = useFilterStore((s) => s.removeFilter);
  const filters = useFilterStore((s) => s.filters);
  const activeFilters = Object.keys(filters).length;

  let pillClass = "topbar-pill is-unknown";
  let pillText = "Connecting…";
  if (status === "error") {
    pillClass = "topbar-pill is-down";
    pillText = "Offline";
  } else if (data?.status === "ok") {
    pillClass = "topbar-pill is-ok";
    pillText = "Connected";
  } else if (data?.status === "degraded") {
    pillClass = "topbar-pill is-degraded";
    pillText = "Degraded";
  }

  // AGENT-L: fire a window-level custom event so Agent R's drawer (mounted
  // elsewhere) can react without us importing its store directly.
  const handleOpenRawTables = () => {
    window.dispatchEvent(new CustomEvent("hsq:open-raw-tables"));
  };

  return (
    <header className="topbar">
      {/* AGENT-L: left side — page title + breadcrumb. */}
      <div className="topbar-left">
        <div className="topbar-title">HSQ</div>
        <div className="topbar-breadcrumb">HSQ Report › Main View</div>
      </div>
      <div className="topbar-right">
        <button
          type="button"
          className="topbar-clear"
          disabled={activeFilters === 0}
          onClick={clearAll}
          title="Clear all cross-filters"
        >
          Clear filters{activeFilters > 0 ? ` (${activeFilters})` : ""}
        </button>
        {/* P1-pills: horizontal scrollable strip, one pill per active filter. */}
        {activeFilters > 0 && (
          <div className="topbar-filter-pills" role="list" aria-label="Active filters">
            {Object.entries(filters).map(([key, entry]) => {
              const label = formatPillLabel(key, entry as FilterEntry);
              return (
                <span key={key} className="topbar-filter-pill" role="listitem" title={label}>
                  <span className="topbar-filter-pill-text">{label}</span>
                  <button
                    type="button"
                    className="topbar-filter-pill-x"
                    aria-label={`Remove filter ${label}`}
                    title="Remove this filter"
                    onClick={() => removeFilter(key)}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <div className={pillClass} title={data?.version ? `v${data.version}` : undefined}>
          <span className="topbar-pill-dot" />
          <span>{isFetching ? "…" : pillText}</span>
        </div>
        {/* AGENT-L: prominent "View Raw Tables" button — emits the
            hsq:open-raw-tables custom event Agent R's drawer subscribes to. */}
        <button
          type="button"
          className="topbar-raw-tables"
          onClick={handleOpenRawTables}
          title="Browse the underlying source tables"
        >
          View Raw Tables
        </button>
        {/* AGENT-A10: link to the rebuilt /dashboard (the modern light-theme
            redesign) — non-breaking, the legacy /p/HSQ remains the default. */}
        <a
          href="/dashboard"
          className="topbar-raw-tables"
          style={{ textDecoration: "none" }}
          title="Open the rebuilt dashboard"
        >
          ↗ New Dashboard
        </a>
      </div>
    </header>
  );
}
